import React from 'react';
import { Filter, Star, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SearchFilters } from '@/types/Bot';
import { useAuth } from '@/contexts/AuthContext';

const categories = [
  'All Categories',
  'productivity',
  'business',
  'coding',
  'fitness',
  'nutrition',
  'marketing',
  'image-generation',
  'video-generation',
  'writing',
  'developer-tools'
];

interface FilterBarProps {
  filters: SearchFilters;
  onFiltersChange: (filters: SearchFilters) => void;
  resultsCount: number;
  isAiSortLoading?: boolean;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  onFiltersChange,
  resultsCount,
  isAiSortLoading = false
}) => {
  const { isPremium } = useAuth();
  const priceOptions = ['Free', 'Freemium', 'Paid'];

  const togglePriceFilter = (price: string) => {
    const newPrices = filters.price.includes(price)
      ? filters.price.filter(p => p !== price)
      : [...filters.price, price];
    
    onFiltersChange({ ...filters, price: newPrices });
  };

  const [isMobileOpen, setIsMobileOpen] = React.useState(false);

  return (
    <>
      {/* Mobile Filter Button */}
      <div className="md:hidden mb-4">
        <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="w-full">
              <Filter className="w-4 h-4 mr-2" />
              Filters
              <Badge variant="secondary" className="ml-auto">
                {resultsCount}
              </Badge>
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-[80vh] overflow-y-auto">
            <SheetHeader>
              <SheetTitle>Filters</SheetTitle>
            </SheetHeader>
            <div className="grid grid-cols-1 gap-4 mt-6">
              {/* Category Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Category</label>
                <Select value={filters.category} onValueChange={(value) => onFiltersChange({ ...filters, category: value })}>
                  <SelectTrigger className="bg-background border-border" aria-label="Select category">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(category => (
                      <SelectItem key={category} value={category}>{category}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Pricing Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Pricing</label>
                <div className="flex flex-wrap gap-2">
                  {priceOptions.map((price) => (
                    <Button
                      key={price}
                      size="sm"
                      variant={filters.price.includes(price) ? "default" : "outline"}
                      onClick={() => togglePriceFilter(price)}
                      aria-label={`Filter by ${price} pricing`}
                    >
                      {price}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Sort Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Sort By</label>
                <Select value={filters.sortBy} onValueChange={(value: any) => onFiltersChange({ ...filters, sortBy: value })}>
                  <SelectTrigger className="bg-background border-border" aria-label="Select sorting method">
                    <SelectValue placeholder="Select sorting" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="relevance">Relevance</SelectItem>
                    <SelectItem value="rating">Highest Rated</SelectItem>
                    <SelectItem value="popularity">Most Popular</SelectItem>
                    <SelectItem value="newest">Newest</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Desktop Filters */}
      <div className="hidden md:block">
        <div className="flex flex-col gap-4 p-4 sm:p-6 bg-card rounded-xl border border-border ">
          <div className="flex items-center gap-2 text-foreground font-medium">
            <Filter className="w-4 h-4" />
            <span className="text-sm sm:text-base">Filters</span>
            <Badge variant="secondary" className="ml-auto text-xs sm:text-sm">
              {resultsCount} results
            </Badge>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Category Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-muted-foreground">Category</label>
          <Select
            value={filters.category}
            onValueChange={(value) => onFiltersChange({ ...filters, category: value })}
          >
            <SelectTrigger className="bg-background/50" aria-label="Select category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categories.map((category) => (
                <SelectItem key={category} value={category}>
                  {category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Price Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-muted-foreground">Price</label>
          <div className="flex flex-wrap gap-2">
            {priceOptions.map((price) => (
              <Button
                key={price}
                size="sm"
                variant={filters.price.includes(price) ? "default" : "outline"}
                onClick={() => togglePriceFilter(price)}
                className="h-8"
                aria-label={`Filter by ${price} pricing`}
              >
                {price}
              </Button>
            ))}
          </div>
        </div>

        {/* Rating Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-muted-foreground">Min Rating</label>
          <Select
            value={filters.minRating.toString()}
            onValueChange={(value) => onFiltersChange({ ...filters, minRating: parseFloat(value) })}
          >
            <SelectTrigger className="bg-background/50" aria-label="Select minimum rating">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Any Rating</SelectItem>
              <SelectItem value="4">4+ <Star className="w-3 h-3 inline ml-1" /></SelectItem>
              <SelectItem value="4.5">4.5+ <Star className="w-3 h-3 inline ml-1" /></SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Sort Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-muted-foreground">Sort By</label>
          <Select
            value={filters.sortBy}
            onValueChange={(value: any) => onFiltersChange({ ...filters, sortBy: value })}
          >
            <SelectTrigger className="bg-background/50" aria-label="Select sorting method">
              <SelectValue />
              {isAiSortLoading && filters.sortBy === 'ai' && (
                <Loader2 className="w-3 h-3 animate-spin ml-1" />
              )}
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="relevance">Relevance</SelectItem>
              <SelectItem value="rating">Highest Rated</SelectItem>
              <SelectItem value="popularity">Most Popular</SelectItem>
              <SelectItem value="newest">Newest</SelectItem>
              
              {/* AI Smart Sort Option */}
              {isPremium ? (
                <SelectItem value="ai">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-3 h-3" />
                    AI Smart Sort
                  </div>
                </SelectItem>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center justify-between px-2 py-1.5 text-sm cursor-not-allowed opacity-50">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-3 h-3" />
                          AI Smart Sort
                        </div>
                        <Badge variant="secondary" className="text-xs">Premium</Badge>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Upgrade to Premium for AI-powered smart sorting</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
    </div>
    </>
  );
};